import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { useVehicleFilter } from '../contexts/VehicleFilterContext'
import { ArrowLeft, Users, CreditCard, Banknote, Car } from 'lucide-react'
import DateFilterBar, { OutputMode } from '../components/DateFilterBar'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { formatNorwegianDate } from '../utils/dateUtils'
import ExportButtons from '../components/ExportButtons'
import { ColumnDefinition } from '../utils/exportToCsv'
import { localizeApiDetail } from '../i18n/apiError'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

// -- Types --

interface AgeDistributionBin {
  age: number
  count: number
  percentage: number
}

interface BrandDistData {
  brand: string
  category: string
  total_count: number
  average_age: number | null
  median_age: number | null
  distribution: AgeDistributionBin[]
}

interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current: PeriodInfo | null
  ytd_previous: PeriodInfo | null
}

type CategoryKey = 'all' | 'leasing' | 'loan'

// Labels are translated at render: t(`detail.categories.${cat}`)
const CATEGORY_CONFIG: Record<CategoryKey, { icon: typeof Car }> = {
  all: { icon: Car },
  leasing: { icon: CreditCard },
  loan: { icon: Banknote },
}

// Colors for chart datasets
const CHART_COLORS = {
  primary: {
    line: 'rgba(59, 130, 246, 1)',
    fill: 'rgba(59, 130, 246, 0.15)',
    point: 'rgba(59, 130, 246, 1)',
  },
  compare: {
    line: 'rgba(239, 68, 68, 1)',
    fill: 'rgba(239, 68, 68, 0.1)',
    point: 'rgba(239, 68, 68, 1)',
  },
}

// -- Component --

export default function AgeAnalyticsBrandDetail() {
  const { t } = useTranslation('age')
  const catLabel = (cat: CategoryKey) => t(`detail.categories.${cat}` as const)
  const { brandName } = useParams<{ brandName: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getVarebilParam } = useVehicleFilter()

  const decodedBrand = decodeURIComponent(brandName || '')
  const initialTab = (searchParams.get('tab') || 'generelt') as 'generelt' | 'leasing' | 'loan'
  const initialMode = (searchParams.get('mode') || 'year_to_date') as OutputMode
  const maxDataDate = useMaxDataDate()

  // Map tab → category
  const tabToCat = (tab: string): CategoryKey => {
    if (tab === 'leasing') return 'leasing'
    if (tab === 'loan') return 'loan'
    return 'all'
  }

  // UI state
  const [activeCategory, setActiveCategory] = useState<CategoryKey>(tabToCat(initialTab))
  const [outputMode, setOutputMode] = useState<OutputMode>(initialMode)
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [compareBrand, setCompareBrand] = useState('')
  const [showPreviousPeriod, setShowPreviousPeriod] = useState(false)

  // Data state
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [primary, setPrimary] = useState<BrandDistData | null>(null)
  const [compare, setCompare] = useState<BrandDistData | null>(null)
  const [periods, setPeriods] = useState<PeriodsInfo | null>(null)

  // Previous period data
  const [prevLoading, setPrevLoading] = useState(false)
  const [prevPrimary, setPrevPrimary] = useState<BrandDistData | null>(null)
  const [prevCompare, setPrevCompare] = useState<BrandDistData | null>(null)

  // Brand list for comparison (from avg-age-by-brand endpoint)
  const [brandList, setBrandList] = useState<string[]>([])

  // Chart ref for PDF image capture
  const chartRef = useRef<ChartJS<'line'>>(null)

  // Fetch brand list on mount
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const varebilParam = getVarebilParam()
        const params: Record<string, any> = { output_mode: 'year_to_date', category: 'all', limit: 200 }
        if (varebilParam !== undefined) params.varebil = varebilParam
        const res = await publicAPI.getAgeAvgByBrand(params)
        const brands = (res.data.data || []).map((r: any) => r.brand).filter((b: string) => b !== decodedBrand).sort()
        setBrandList(brands)
      } catch {
        // non-critical
      }
    }
    fetchBrands()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -- Fetch main distribution --
  const fetchDistribution = async () => {
    if (!decodedBrand) return
    try {
      setLoading(true)
      setError(null)

      const varebilParam = getVarebilParam()
      const params: {
        brand: string
        category: string
        output_mode: string
        compare_brand?: string
        varebil?: boolean
        mine_postnummer?: string
        custom_start_date?: string
        custom_end_date?: string
      } = {
        brand: decodedBrand,
        category: activeCategory,
        output_mode: outputMode,
      }
      if (outputMode === 'custom' && customStartDate && customEndDate) {
        params.custom_start_date = customStartDate
        params.custom_end_date = customEndDate
      }
      if (varebilParam !== undefined) params.varebil = varebilParam
      if (compareBrand) params.compare_brand = compareBrand

      const res = await publicAPI.getBrandAgeDistribution(params)
      setPrimary(res.data.primary || null)
      setCompare(res.data.compare || null)
      setPeriods(res.data.periods || null)
    } catch (err: any) {
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('detail.loadError'))
    } finally {
      setLoading(false)
    }
  }

  // -- Fetch previous period distribution (for YoY comparison) --
  const fetchPreviousPeriod = async () => {
    if (!decodedBrand || !periods?.previous) return
    try {
      setPrevLoading(true)
      const varebilParam = getVarebilParam()
      const params: {
        brand: string
        category: string
        start_date?: string
        end_date?: string
        compare_brand?: string
        varebil?: boolean
        mine_postnummer?: string
      } = {
        brand: decodedBrand,
        category: activeCategory,
        start_date: periods.previous.start_date,
        end_date: periods.previous.end_date,
      }
      if (varebilParam !== undefined) params.varebil = varebilParam
      if (compareBrand) params.compare_brand = compareBrand

      const res = await publicAPI.getBrandAgeDistribution(params)
      setPrevPrimary(res.data.primary || null)
      setPrevCompare(res.data.compare || null)
    } catch {
      // non-critical
      setPrevPrimary(null)
      setPrevCompare(null)
    } finally {
      setPrevLoading(false)
    }
  }

  useEffect(() => {
    fetchDistribution()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, compareBrand, outputMode])

  useEffect(() => {
    if (showPreviousPeriod && periods?.previous) {
      fetchPreviousPeriod()
    } else {
      setPrevPrimary(null)
      setPrevCompare(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreviousPeriod, periods])

  const handleApply = () => {
    if (outputMode === 'custom' && (!customStartDate || !customEndDate)) return
    fetchDistribution()
  }

  // -- Chart data --

  const buildChartData = useMemo(() => {
    if (!primary) return null

    // Build age labels from 18 to 90
    const allAges = Array.from({ length: 73 }, (_, i) => i + 18)
    const labels = allAges.map(a => a.toString())

    // Helper: map distribution array to ordered array by age
    const mapDist = (dist: AgeDistributionBin[]) => {
      const map = new Map(dist.map(d => [d.age, d.percentage]))
      return allAges.map(a => map.get(a) || 0)
    }

    const datasets: any[] = [
      {
        label: `${primary.brand} (${t(`detail.categories.${activeCategory}` as const)})`,
        data: mapDist(primary.distribution),
        borderColor: CHART_COLORS.primary.line,
        backgroundColor: CHART_COLORS.primary.fill,
        pointBackgroundColor: CHART_COLORS.primary.point,
        pointRadius: 1.5,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      },
    ]

    if (compare) {
      datasets.push({
        label: `${compare.brand} (${t(`detail.categories.${activeCategory}` as const)})`,
        data: mapDist(compare.distribution),
        borderColor: CHART_COLORS.compare.line,
        backgroundColor: CHART_COLORS.compare.fill,
        pointBackgroundColor: CHART_COLORS.compare.point,
        pointRadius: 1.5,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      })
    }

    // Previous period datasets
    if (showPreviousPeriod && prevPrimary) {
      datasets.push({
        label: `${prevPrimary.brand} (${periods?.previous.label || t('detail.previousShort')})`,
        data: mapDist(prevPrimary.distribution),
        borderColor: 'rgba(59, 130, 246, 0.4)',
        backgroundColor: 'transparent',
        pointBackgroundColor: 'rgba(59, 130, 246, 0.4)',
        pointRadius: 1,
        borderWidth: 1.5,
        borderDash: [6, 3],
        fill: false,
        tension: 0.4,
      })
    }

    if (showPreviousPeriod && prevCompare) {
      datasets.push({
        label: `${prevCompare.brand} (${periods?.previous.label || t('detail.previousShort')})`,
        data: mapDist(prevCompare.distribution),
        borderColor: 'rgba(239, 68, 68, 0.4)',
        backgroundColor: 'transparent',
        pointBackgroundColor: 'rgba(239, 68, 68, 0.4)',
        pointRadius: 1,
        borderWidth: 1.5,
        borderDash: [6, 3],
        fill: false,
        tension: 0.4,
      })
    }

    return { labels, datasets }
  }, [primary, compare, prevPrimary, prevCompare, activeCategory, showPreviousPeriod, periods, t])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`,
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: t('detail.axisAge') },
        ticks: {
          callback: function (this: any, _val: any, index: number) {
            const age = index + 18
            return age % 5 === 0 ? age.toString() : ''
          },
          maxRotation: 0,
        },
      },
      y: {
        title: { display: true, text: t('detail.axisShare') },
        beginAtZero: true,
        ticks: {
          callback: (value: number | string) => `${value}%`,
        },
      },
    },
  }

  // -- Export data (CSV + PDF) --

  const exportData = useMemo(() => {
    if (!primary) return { data: [], columns: [] as ColumnDefinition[] }

    const allAges = Array.from({ length: 73 }, (_, i) => i + 18)
    const primaryMap = new Map(primary.distribution.map(d => [d.age, d]))
    const compareMap = compare ? new Map(compare.distribution.map(d => [d.age, d])) : null
    const prevPrimaryMap = prevPrimary ? new Map(prevPrimary.distribution.map(d => [d.age, d])) : null
    const prevCompareMap = prevCompare ? new Map(prevCompare.distribution.map(d => [d.age, d])) : null

    const prevLabel = periods?.previous.label || t('detail.previousShort')
    const group = t(`detail.categories.${activeCategory}` as const)
    const countCol = (series: string, grp: string) => t('detail.export.colCount', { series, group: grp })
    const shareCol = (series: string, grp: string) => t('detail.export.colShare', { series, group: grp })

    const columns: ColumnDefinition[] = [
      { key: 'alder', label: t('detail.export.colAge') },
      { key: 'p_antall', label: countCol(primary.brand, group) },
      { key: 'p_andel', label: shareCol(primary.brand, group) },
    ]
    if (compare) {
      columns.push({ key: 'c_antall', label: countCol(compare.brand, group) })
      columns.push({ key: 'c_andel', label: shareCol(compare.brand, group) })
    }
    if (showPreviousPeriod && prevPrimary) {
      columns.push({ key: 'pp_antall', label: countCol(primary.brand, prevLabel) })
      columns.push({ key: 'pp_andel', label: shareCol(primary.brand, prevLabel) })
    }
    if (showPreviousPeriod && prevCompare) {
      columns.push({ key: 'pc_antall', label: countCol(`${compare?.brand}`, prevLabel) })
      columns.push({ key: 'pc_andel', label: shareCol(`${compare?.brand}`, prevLabel) })
    }

    const fmt1 = (v: number | null | undefined) =>
      v != null ? v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'
    const fmtInt = (v: number) => v.toLocaleString('nb-NO')

    const summaryRows: Record<string, any>[] = [
      {
        alder: t('detail.count'), p_antall: fmtInt(primary.total_count), p_andel: '',
        ...(compare ? { c_antall: fmtInt(compare.total_count), c_andel: '' } : {}),
        ...(showPreviousPeriod && prevPrimary ? { pp_antall: fmtInt(prevPrimary.total_count), pp_andel: '' } : {}),
        ...(showPreviousPeriod && prevCompare ? { pc_antall: fmtInt(prevCompare.total_count), pc_andel: '' } : {}),
      },
      {
        alder: t('detail.avgAge'), p_antall: fmt1(primary.average_age), p_andel: '',
        ...(compare ? { c_antall: fmt1(compare.average_age), c_andel: '' } : {}),
        ...(showPreviousPeriod && prevPrimary ? { pp_antall: fmt1(prevPrimary.average_age), pp_andel: '' } : {}),
        ...(showPreviousPeriod && prevCompare ? { pc_antall: fmt1(prevCompare.average_age), pc_andel: '' } : {}),
      },
      {
        alder: t('detail.medianAge'), p_antall: fmt1(primary.median_age), p_andel: '',
        ...(compare ? { c_antall: fmt1(compare.median_age), c_andel: '' } : {}),
        ...(showPreviousPeriod && prevPrimary ? { pp_antall: fmt1(prevPrimary.median_age), pp_andel: '' } : {}),
        ...(showPreviousPeriod && prevCompare ? { pc_antall: fmt1(prevCompare.median_age), pc_andel: '' } : {}),
      },
      { alder: '' }, // separator
    ]

    const distRows: Record<string, any>[] = allAges.map(age => {
      const pb = primaryMap.get(age)
      const row: Record<string, any> = {
        alder: age,
        p_antall: pb?.count ?? 0,
        p_andel: pb ? pb.percentage.toFixed(2) : '0.00',
      }
      if (compare && compareMap) {
        const cb = compareMap.get(age)
        row.c_antall = cb?.count ?? 0
        row.c_andel = cb ? cb.percentage.toFixed(2) : '0.00'
      }
      if (showPreviousPeriod && prevPrimary && prevPrimaryMap) {
        const pb2 = prevPrimaryMap.get(age)
        row.pp_antall = pb2?.count ?? 0
        row.pp_andel = pb2 ? pb2.percentage.toFixed(2) : '0.00'
      }
      if (showPreviousPeriod && prevCompare && prevCompareMap) {
        const pc = prevCompareMap.get(age)
        row.pc_antall = pc?.count ?? 0
        row.pc_andel = pc ? pc.percentage.toFixed(2) : '0.00'
      }
      return row
    })

    return { data: [...summaryRows, ...distRows], columns }
  }, [primary, compare, prevPrimary, prevCompare, showPreviousPeriod, periods, activeCategory, t])

  // -- Render --

  return (
    <div className="space-y-6">
      {/* Breadcrumb / Back */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/age-analytics')}
          className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('detail.backToOverview')}
        </button>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('detail.title', { label: decodedBrand })}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('detail.subtitleBrand', { label: decodedBrand })}
        </p>
      </div>

      {/* Category buttons */}
      <div className="flex gap-2">
        {(Object.keys(CATEGORY_CONFIG) as CategoryKey[]).map(cat => {
          const { icon: Icon } = CATEGORY_CONFIG[cat]
          const label = catLabel(cat)
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeCategory === cat
                  ? 'bg-primary-600 text-white shadow-xs'
                  : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          )
        })}
      </div>

      {/* Period selector */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={(mode) => {
          setOutputMode(mode)
          if (mode !== 'custom') setTimeout(() => fetchDistribution(), 0)
        }}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApply}
        maxDataDate={maxDataDate}
      />

      {/* Period info + loan warning */}
      {periods && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="font-semibold text-blue-800 dark:text-blue-300">{periods.current.label}:</span>{' '}
                <span className="text-blue-700 dark:text-blue-400">{formatNorwegianDate(periods.current.start_date)} &rarr; {formatNorwegianDate(periods.current.end_date)}</span>
              </div>
              <div>
                <span className="font-semibold text-blue-800 dark:text-blue-300">{periods.previous.label}:</span>{' '}
                <span className="text-blue-700 dark:text-blue-400">{formatNorwegianDate(periods.previous.start_date)} &rarr; {formatNorwegianDate(periods.previous.end_date)}</span>
              </div>
            </div>
            {activeCategory === 'loan' && (
              <p className="text-blue-800 dark:text-blue-200">
                <Trans t={t} i18nKey="detail.loanNote" components={{ b: <strong /> }} />
              </p>
            )}
          </div>
        </div>
      )}

      {/* Comparison controls */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-6">
          {/* Compare brand */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('detail.compareWith')}
            </label>
            <select
              value={compareBrand}
              onChange={e => setCompareBrand(e.target.value)}
              className="input-field text-sm px-3 py-1.5 min-w-[200px]"
            >
              <option value="">{t('detail.noComparison')}</option>
              {brandList.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          {/* Toggle previous period */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPreviousPeriod}
              onChange={e => setShowPreviousPeriod(e.target.checked)}
              className="w-4 h-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('detail.showPrevious')}
            </span>
          </label>
        </div>
      </div>

      {/* Stats summary cards — up to 4: current primary, current compare, prev primary, prev compare */}
      {!loading && primary && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* Primary brand stats — current period */}
          <div className="card p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{primary.brand}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{periods?.current.label || t('detail.currentPeriod')}</p>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('detail.count')}</span>
                <span className="font-semibold">{primary.total_count.toLocaleString('nb-NO')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('detail.avgAge')}</span>
                <span className="font-semibold text-primary-600 dark:text-primary-400">
                  {primary.average_age !== null ? primary.average_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('detail.medianAge')}</span>
                <span className="font-semibold">
                  {primary.median_age !== null ? primary.median_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Compare brand stats — current period */}
          {compare && (
            <div className="card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <Users className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{compare.brand}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{periods?.current.label || t('detail.currentPeriod')}</p>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.count')}</span>
                  <span className="font-semibold">{compare.total_count.toLocaleString('nb-NO')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.avgAge')}</span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {compare.average_age !== null ? compare.average_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.medianAge')}</span>
                  <span className="font-semibold">
                    {compare.median_age !== null ? compare.median_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Primary brand — previous period */}
          {showPreviousPeriod && prevPrimary && (
            <div className="card p-5 opacity-80">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <Users className="w-5 h-5 text-blue-400 dark:text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{prevPrimary.brand}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{periods?.previous.label || t('detail.previousPeriod')}</p>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.count')}</span>
                  <span className="font-semibold">{prevPrimary.total_count.toLocaleString('nb-NO')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.avgAge')}</span>
                  <span className="font-semibold text-blue-400 dark:text-blue-500">
                    {prevPrimary.average_age !== null ? prevPrimary.average_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.medianAge')}</span>
                  <span className="font-semibold">
                    {prevPrimary.median_age !== null ? prevPrimary.median_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Compare brand — previous period */}
          {showPreviousPeriod && prevCompare && (
            <div className="card p-5 opacity-80">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <Users className="w-5 h-5 text-red-400 dark:text-red-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{prevCompare.brand}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{periods?.previous.label || t('detail.previousPeriod')}</p>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.count')}</span>
                  <span className="font-semibold">{prevCompare.total_count.toLocaleString('nb-NO')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.avgAge')}</span>
                  <span className="font-semibold text-red-400 dark:text-red-500">
                    {prevCompare.average_age !== null ? prevCompare.average_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('detail.medianAge')}</span>
                  <span className="font-semibold">
                    {prevCompare.median_age !== null ? prevCompare.median_age.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export buttons */}
      {!loading && primary && (
        <div className="flex justify-end">
          <ExportButtons
            data={exportData.data}
            columns={exportData.columns}
            metadata={{
              title: t('detail.export.title', { label: decodedBrand, category: catLabel(activeCategory) }),
              dateRange: periods
                ? `${formatNorwegianDate(periods.current.start_date)} - ${formatNorwegianDate(periods.current.end_date)}`
                : '',
              filters: [
                t('detail.export.filterBrand', { value: decodedBrand }),
                t('detail.export.filterCategory', { value: catLabel(activeCategory) }),
                ...(compare ? [t('detail.export.filterCompareBrand', { value: compare.brand })] : []),
                ...(showPreviousPeriod ? [t('detail.export.filterIncludesPrevious', { value: periods?.previous.label || '' })] : []),
              ],
            }}
            onGetChartImage={() => chartRef.current?.toBase64Image('image/png', 1.0) ?? undefined}
            disabled={loading || prevLoading || exportData.data.length === 0}
          />
        </div>
      )}

      {/* Main chart */}
      <div className="card p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
          {t('detail.chartTitle', { category: catLabel(activeCategory) })}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('detail.chartDescription')}
          {showPreviousPeriod && t('detail.chartDashed')}
          {compare && t('detail.chartRedLine', { label: compare.brand })}
        </p>

        {loading || prevLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage message={error} />
        ) : buildChartData ? (
          <div style={{ height: '450px' }}>
            <Line ref={chartRef} data={buildChartData} options={chartOptions} />
          </div>
        ) : (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('detail.noData')}</p>
        )}
      </div>

      {/* All three categories overview */}
      {!loading && primary && (
        <div className="card p-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('detail.quickOverview', { label: decodedBrand })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(Object.keys(CATEGORY_CONFIG) as CategoryKey[]).map(cat => {
              const { icon: Icon } = CATEGORY_CONFIG[cat]
              const label = catLabel(cat)
              const isActive = cat === activeCategory
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    isActive
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-dark-border hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'}`} />
                    <span className={`font-semibold ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}>
                      {label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('detail.clickToShow', { category: label.toLowerCase() })}
                  </p>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
